import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action, notifyPropertyChange } from '@ember/object';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import didInsert from '@ember/render-modifiers/modifiers/did-insert';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import didUpdate from '@ember/render-modifiers/modifiers/did-update';
import { service } from '@ember/service';
import type StoreService from '@ember-data/store';
import Component from '@glimmer/component';
import CopyButton from 'ember-cli-clipboard/components/copy-button';
import { task } from 'ember-concurrency';
import { and, eq, lt, or } from 'ember-truth-helpers';
import CloseButton from 'garaje/components/close-button';
import ModalDialog from 'garaje/components/ui/classic/modal-dialog';
import config from 'garaje/config/environment';
import type AjaxService from 'garaje/services/ajax';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type StateService from 'garaje/services/state';
import type StatsigService from 'garaje/services/statsig';
import { RoleScopeType } from 'garaje/utils/custom-roles';
import urlBuilder from 'garaje/utils/url-builder';
import { tracked } from 'tracked-built-ins';
import { cached } from 'tracked-toolbox';

interface ShareKioskDemoArgs {
  onClose: () => void;
  demoName: string;
}

interface NewAdmin {
  name: string;
  email: string;
  exists: boolean;
}

interface EmployeeData {
  attributes: {
    email: string;
    name: string;
  };
  relationships: {
    company: { data: { type: string; id: string } };
    locations: { data: { type: string; id: string }[] };
    'role-assignments': {
      data: {
        type: string;
        attributes: { 'role-id': string; 'role-scope-id': number; 'role-scope-type': RoleScopeType };
      }[];
    };
  };
  type: string;
}

const PREVIEW_TEXT = 'Copy preview link';
const SHARE_PREVIEW_COPIED_TEXT = 'Link copied';
const LINK_IMG_SRC = '/assets/images/ipad-demo/icons/chain.svg';
const LINK_COPIED_IMG_SRC = '/assets/images/ipad-demo/icons/check.svg';

export default class ShareKioskDemo extends Component<ShareKioskDemoArgs> {
  @service declare state: StateService;
  @service declare statsig: StatsigService;
  @service declare store: StoreService;
  @service declare ajax: AjaxService;
  @service declare flashMessages: FlashMessagesService;

  @tracked copyPreviewLinkText: string = PREVIEW_TEXT;
  @tracked copyPreviewLinkImageSrc: string = LINK_IMG_SRC;
  @tracked teamMembersToInvite: Array<NewAdmin> = [{ name: '', email: '', exists: false }];

  @tracked emailErrors: { [key: number]: string } = {};
  @tracked nameErrors: { [key: number]: string } = {};

  @tracked shouldShowSubmitButton = false;
  @tracked showDeleteButtons = false;

  constructor(owner: unknown, args: ShareKioskDemoArgs) {
    super(owner, args);
    this.statsig.logEvent('kiosk-demo-share-modal-viewed', null, { demoName: this.args.demoName });
  }

  @cached
  get teamMembersCount(): number {
    return this.teamMembersToInvite.length;
  }

  get shareLink(): string {
    const { currentCompany } = this.state;
    return `${config.host}/ipad-demo?cid=${currentCompany?.id}`;
  }

  get hasAnyErrors(): boolean {
    return Boolean(Object.keys(this.emailErrors).length) || Boolean(Object.keys(this.nameErrors).length);
  }

  @action errorMessage(index: number, value: 'email' | 'name'): string {
    if (value === 'email') {
      return this.emailErrors[index] || '';
    } else {
      return this.nameErrors[index] || '';
    }
  }

  @action copyShareLink(): void {
    this.statsig.logEvent(`kiosk-demo-${this.args.demoName}-share-link-clicked`, null, { source: 'share-modal' });
    this.copyPreviewLinkText = SHARE_PREVIEW_COPIED_TEXT;
    this.copyPreviewLinkImageSrc = LINK_COPIED_IMG_SRC;

    setTimeout(() => {
      this.copyPreviewLinkText = PREVIEW_TEXT;
      this.copyPreviewLinkImageSrc = LINK_IMG_SRC;
    }, 2000);
  }

  @action handleEmailChange(idx: number, event: Event): void {
    const target = event.target as HTMLInputElement;
    const email = target.value;
    this.teamMembersToInvite[idx]!.email = email;
    this.checkShouldShowSubmit();
    if (this.emailErrors[idx]) {
      delete this.emailErrors[idx];
      notifyPropertyChange(this, 'emailErrors');
    }
    if (!email.length && this.nameErrors[idx] && this.nameErrors[idx] === "Full name can't be blank") {
      delete this.nameErrors[idx];
      notifyPropertyChange(this, 'nameErrors');
    }
    if (this.teamMembersToInvite[idx]!.exists) {
      const newTeamMember = { ...this.teamMembersToInvite[idx]!, exists: false };
      this.teamMembersToInvite = [
        ...this.teamMembersToInvite.slice(0, idx),
        newTeamMember,
        ...this.teamMembersToInvite.slice(idx + 1),
      ];
    }
  }

  @action handleNameChange(idx: number, event: Event): void {
    const target = event.target as HTMLInputElement;
    const name = target.value;
    this.teamMembersToInvite[idx]!.name = name;
    this.checkShouldShowSubmit();
    if (this.nameErrors[idx]) {
      delete this.nameErrors[idx];
      notifyPropertyChange(this, 'nameErrors');
    }
  }

  @action checkShouldShowSubmit(): void {
    if (this.teamMembersToInvite.some((member) => member.name && member.email)) {
      this.shouldShowSubmitButton = true;
    } else {
      this.shouldShowSubmitButton = false;
    }
  }

  isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  @action handleEmailFocusOut(idx: number): void {
    const currentMember = this.teamMembersToInvite[idx]!;
    if (!currentMember.email.length || this.isValidEmail(currentMember.email)) {
      if (
        (currentMember.email.length && !currentMember.exists) ||
        (!currentMember.email.length && currentMember.exists)
      ) {
        void this.findEmployeeTask.perform(idx);
      }
    } else {
      this.emailErrors[idx] = 'Enter a valid email address';
      notifyPropertyChange(this, 'emailErrors');
    }
  }

  @action handleNameFocusOut(idx: number): void {
    const currentMember = this.teamMembersToInvite[idx]!;
    if (currentMember.email.length && !currentMember.name.length) {
      this.nameErrors[idx] = "Full name can't be blank";
      notifyPropertyChange(this, 'nameErrors');
    }
  }

  @action handleAddMoreClicked(): void {
    this.teamMembersToInvite = [...this.teamMembersToInvite, { name: '', email: '', exists: false }];
    this.showDeleteButtons = true;
    this.statsig.logEvent('kiosk-demo-share-modal-add-another-clicked');
  }

  @action deleteRow(idx: number): void {
    this.teamMembersToInvite = this.teamMembersToInvite.filter((_, index) => index !== idx);
    if (this.teamMembersToInvite.length === 1) {
      this.showDeleteButtons = false;
    }
    this.checkShouldShowSubmit();
  }

  @action
  scrollToBottom(element: HTMLElement): void {
    requestAnimationFrame(() => {
      element.scrollTop = element.scrollHeight;
    });
  }

  @action
  focusOnEmail(element: HTMLElement): void {
    if (element) {
      (element as HTMLInputElement).focus();
    }
  }

  findEmployeeTask = task(async (idx: number) => {
    const emailToFind = this.teamMembersToInvite[idx]!.email;
    const employees = await this.store.query('employee', {
      filter: {
        deleted: false,
        email: emailToFind,
      },
    });

    const foundEmployee = employees.find((result) => result.email === emailToFind);
    if (foundEmployee) {
      const newTeamMember = { ...this.teamMembersToInvite[idx]!, name: foundEmployee.name, exists: true };
      this.teamMembersToInvite = [
        ...this.teamMembersToInvite.slice(0, idx),
        newTeamMember,
        ...this.teamMembersToInvite.slice(idx + 1),
      ];
      this.checkShouldShowSubmit();
      this.statsig.logEvent('kiosk-demo-share-modal-employee-found', null, {
        email: emailToFind,
        name: foundEmployee.name,
        id: foundEmployee.id,
      });
    }
  });

  submitTask = task({ drop: true }, async () => {
    const { currentCompany, currentLocation } = this.state;

    let existCount = 0;
    let rowCount = 0;
    const employeesData: EmployeeData[] = [];
    this.teamMembersToInvite.forEach((member) => {
      if (member.email.length && member.name.length) {
        if (member.exists) {
          existCount++;
        }
        rowCount++;
        employeesData.push({
          attributes: {
            email: member.email,
            name: member.name,
          },
          relationships: {
            company: {
              data: {
                type: 'companies',
                id: currentCompany.id,
              },
            },
            locations: {
              data: [
                {
                  type: 'locations',
                  id: currentLocation.id,
                },
              ],
            },
            'role-assignments': {
              data: [
                {
                  type: 'role-assignments',
                  attributes: {
                    'role-id': 'role_globalAdmin',
                    'role-scope-id': parseInt(currentCompany.id),
                    'role-scope-type': RoleScopeType.COMPANY,
                  },
                },
              ],
            },
          },
          type: 'employees',
        });
      }
    });

    this.statsig.logEvent('kiosk-demo-share-modal-submit-clicked', null, {
      demoName: this.args.demoName,
      rowCount,
      existCount,
    });

    const url = urlBuilder.v3.employees.bulkUrl();
    try {
      await this.ajax.request(url, {
        type: 'POST',
        headers: { accept: 'application/vnd.api+json' },
        contentType: 'application/vnd.api+json',
        data: JSON.stringify({
          data: employeesData,
        }),
      });
      this.flashMessages.showAndHideFlash('success', 'Invites sent', 'Admins will receive an email invitation');
      this.args.onClose();
    } catch (_error) {
      this.flashMessages.showAndHideFlash('error', 'Error', 'Something went wrong. Please try again');
      this.args.onClose();
    }
  });

  <template>
    <ModalDialog
      @overlayClass="ember-modal-overlay--fadeIn"
      @targetAttachment="center"
      @translucentOverlay={{true}}
      @containerClass="!max-w-[640px] !min-h-[491px] !overflow-y-auto"
    >
      <CloseButton class="right-8 top-8 !bg-white !p-0 w-10 h-10 fixed" style="min-width:0" @onClick={{@onClose}} />

      <div class="p-6" data-test-kiosk-demo-share-modal>
        <p class="text-2xl font-bold mb-6">Share preview</p>

        <p class="text-base font-bold mb-1">Invite team members as admin</p>
        <p class="text-base font-normal mb-4">Team members will receive an email invitation to join Envoy as global
          admins.</p>

        <div class="w-full flex flex-col overflow-y-auto" {{didUpdate this.scrollToBottom this.teamMembersCount}}>
          {{#each this.teamMembersToInvite as |member index|}}
            <div class="flex">
              <div class="w-full flex space-x-3">
                <label class="w-[60%]">
                  {{#if (eq index 0)}}
                    Email
                  {{/if}}
                  <input
                    class="!mb-2 {{if (this.errorMessage index 'email') 'error'}}"
                    type="email"
                    name="email"
                    placeholder="name@example.com"
                    value={{member.email}}
                    maxlength="70"
                    {{on "focusout" (fn this.handleEmailFocusOut index)}}
                    {{on "input" (fn this.handleEmailChange index)}}
                    {{didInsert this.focusOnEmail}}
                    data-test-demo-share-email-input
                  />
                  <p class="mb-0 text-papaya-50">{{this.errorMessage index "email"}}</p>
                </label>
                <label class="w-[40%]">
                  {{#if (eq index 0)}}
                    Full name
                  {{/if}}
                  <input
                    class="!mb-2 {{if (this.errorMessage index 'name') 'error'}}"
                    disabled={{member.exists}}
                    type="text"
                    value={{member.name}}
                    maxlength="70"
                    {{on "focusout" (fn this.handleNameFocusOut index)}}
                    {{on "input" (fn this.handleNameChange index)}}
                    data-test-demo-share-name-input
                  />
                  <p class="mb-0 text-papaya-50">{{this.errorMessage index "name"}}</p>
                </label>
              </div>

              {{#if this.showDeleteButtons}}
                <button
                  class="ml-2
                    {{if (eq index 0) 'mt-4'}}
                    {{if (or (this.errorMessage index 'email') (this.errorMessage index 'name')) 'mb-b'}}
                    "
                  {{on "click" (fn this.deleteRow index)}}
                >
                  <img src="/assets/images/close-rounded.svg" alt="delete" class="hover:bg-[#f5f5f7] rounded-lg" />
                </button>
              {{/if}}
            </div>
          {{/each}}
        </div>

        {{#if (lt this.teamMembersToInvite.length 8)}}
          <a {{on "click" this.handleAddMoreClicked}} class="flex items-center space-x-2 w-[100px]">
            <img src="/assets/images/add-circle-purple.svg" alt="add-circle" />
            <p class="mb-0 mt-1 text-sm">Add another</p>
          </a>
        {{else}}
          {{! for spacing }}
          <div class="mt-12"></div>
        {{/if}}

        <p class="text-base font-bold mt-10 mb-2">Share link</p>
        <CopyButton @clipboardText={{this.shareLink}} @success={{this.copyShareLink}} class="btn btn--outline">
          <span class="flex items-center">
            <img
              class="pb-[2px] pr-[5px]"
              src={{this.copyPreviewLinkImageSrc}}
              alt="link img"
              data-test-kiosk-demo-copy-link
            />
            <span>{{this.copyPreviewLinkText}}</span>
          </span>
        </CopyButton>
      </div>

      {{#if (and this.shouldShowSubmitButton)}}
        <button
          class="btn btn--small btn--brand right-8 bottom-8 fixed"
          disabled={{or this.hasAnyErrors this.submitTask.isRunning}}
          {{on "click" this.submitTask.perform}}
          data-test-demo-share-send-invite-button
        >
          {{#if this.submitTask.isRunning}}
            Sending...
          {{else}}
            Send invites
          {{/if}}
        </button>
      {{else}}
        <button
          class="btn btn--small btn--brand right-8 bottom-8 fixed"
          {{on "click" @onClose}}
          data-test-demo-share-done-button
        >
          Done
        </button>
      {{/if}}
    </ModalDialog>
  </template>
}
